import React, { useState, useEffect } from 'react';
import { X, Bell, BellOff, Monitor, MonitorOff, Sun, Moon, Laptop, Settings, Trash2, AlertTriangle, CreditCard, Calendar, LogIn, LogOut, User } from 'lucide-react';
import { 
  getNotificationPermission, 
  requestNotificationPermission,
  scheduleDailyReminder,
  clearNotifications,
  areNotificationsDisabled,
  enableNotifications,
  getReminderTimePreference,
  setReminderTimePreference,
  shouldShowNotifications
} from '../../utils/notifications';
import {
  isWakeLockSupported,
  getKeepScreenAwakePreference,
  setKeepScreenAwakePreference
} from '../../utils/screenWakeLock';
import { useTheme, ThemeMode } from '../../contexts/ThemeContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeClick?: () => void;
  onShowDebugPanel?: () => void;
  onShowNotifications?: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  onUpgradeClick,
  onShowDebugPanel,
  onShowNotifications
}) => {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [reminderTime, setReminderTime] = useState(getReminderTimePreference());
  const [notificationsDisabled, setNotificationsDisabled] = useState(areNotificationsDisabled());
  const [keepScreenAwake, setKeepScreenAwake] = useState(getKeepScreenAwakePreference());
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const { theme, setTheme } = useTheme();
  const { isPremiumUser, isTrialActive, trialEndDate, daysRemaining } = useSubscription();
  const auth = useAuth();

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await auth.signIn();
      // Show success message
      alert('Sign-in successful!');
    } catch (error) {
      console.error('Sign-in failed:', error);
      // Show detailed error in alert for debugging
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert(`Sign-in failed: ${errorMsg}\n\nCheck auth.error in settings for more details.`);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Sign-out failed:', error);
    }
  };

  useEffect(() => {
    const checkPermission = () => {
      setPermission(getNotificationPermission());
    };

    if (isOpen) {
      checkPermission();
      setNotificationsDisabled(areNotificationsDisabled());
      document.addEventListener('visibilitychange', checkPermission);
    }

    return () => {
      document.removeEventListener('visibilitychange', checkPermission);
    };
  }, [isOpen]);

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setPermission(getNotificationPermission());
    
    if (granted) {
      enableNotifications();
      setNotificationsDisabled(false);
      const [hour, minute] = reminderTime.split(':').map(Number);
      scheduleDailyReminder(hour, minute);
    }
  };

  const handleDisableNotifications = () => {
    clearNotifications();
    setNotificationsDisabled(true);
  };

  const handleTimeChange = (newTime: string) => {
    setReminderTime(newTime);
    setReminderTimePreference(newTime);
    if (permission.granted && !notificationsDisabled) {
      const [hour, minute] = newTime.split(':').map(Number);
      scheduleDailyReminder(hour, minute);
    }
  };

  const handleKeepScreenAwakeToggle = (enabled: boolean) => {
    setKeepScreenAwake(enabled);
    setKeepScreenAwakePreference(enabled);
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      if (!auth.user?.email) {
        throw new Error('User not authenticated');
      }

      // Call Netlify function to cancel via Stripe API
      const response = await fetch('/.netlify/functions/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: auth.user.email
        })
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      alert('Your subscription has been cancelled. You will retain access until the end of your billing period.');
      setShowCancelConfirmation(false);
      onClose();
      window.location.reload(); // Refresh to update subscription state
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResetAllData = () => {
    // Clear all localStorage
    localStorage.clear();

    // Reload the page to reset the app state
    window.location.reload();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto transition-colors duration-300">
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-600 transition-colors duration-300">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Account Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Account</h3>

            {auth.isAuthenticated && auth.user ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {auth.user.picture ? (
                    <img
                      src={auth.user.picture}
                      alt={auth.user.name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-green-200 dark:bg-green-700 flex items-center justify-center">
                      <User className="w-5 h-5 text-green-700 dark:text-green-200" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-green-900 dark:text-green-100 truncate">{auth.user.name}</p>
                    <p className="text-sm text-green-800 dark:text-green-200 truncate">{auth.user.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <LogIn className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900 dark:text-blue-100">Sign in with Google</p>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                      Sign in to sync your prayer progress across devices and access premium features.
                    </p>
                    <button
                      onClick={handleSignIn}
                      disabled={isSigningIn || auth.isLoading}
                      className="mt-3 flex items-center gap-2 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {isSigningIn ? 'Signing in...' : 'Sign in with Google'}
                    </button>
                    {auth.error && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-xs">
                        <p className="font-medium text-red-800 dark:text-red-300">Error Details:</p>
                        <p className="text-red-700 dark:text-red-400 mt-1 break-words">{auth.error}</p>
                        <p className="text-red-600 dark:text-red-500 mt-1 text-[10px]">
                          Auth State: {JSON.stringify({ isLoading: auth.isLoading, isAuthenticated: auth.isAuthenticated })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notification Settings - Desktop Only */}
          {shouldShowNotifications() && (
          <div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Prayer Reminders</h3>
            
            {permission.denied ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                  <BellOff className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Notifications Blocked</p>
                    <p className="text-sm mt-1">
                      Enable notifications in your browser settings to receive prayer reminders.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className={`w-5 h-5 ${permission.granted && !notificationsDisabled ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className="font-medium text-gray-700 dark:text-gray-200">Daily Notifications</span>
                  </div>
                  
                  {!permission.granted ? (
                    <button
                      onClick={handleEnableNotifications}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Enable
                    </button>
                  ) : notificationsDisabled ? (
                    <button
                      onClick={handleEnableNotifications}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Enable
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-green-600 dark:text-green-400 font-medium">✓ Enabled</span>
                      <button
                        onClick={handleDisableNotifications}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        Disable
                      </button>
                    </div>
                  )}
                </div>

                {permission.granted && !notificationsDisabled && (
                  <div className="pl-8 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Reminder Time:</label>
                      <input
                        type="time"
                        value={reminderTime}
                        onChange={(e) => handleTimeChange(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 dark:[color-scheme:dark]"
                      />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      You'll receive daily reminders to pray your novena at this time.
                    </p>
                  </div>
                )}
                
                {permission.granted && notificationsDisabled && (
                  <div className="pl-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Notifications are currently disabled. Click "Enable" above to turn them back on.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Enhanced Mobile Notifications */}
          {!shouldShowNotifications() && (
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6 transition-colors duration-300">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Enhanced Prayer Reminders</h3>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-900 dark:text-blue-100">Advanced Notification System</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                    Set up multiple daily prayer reminders with our new Capacitor-powered notification system.
                    Works on web, Android, and iOS.
                  </p>
                  {onShowNotifications && (
                    <button
                      onClick={onShowNotifications}
                      className="mt-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Configure Notifications
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Screen Wake Lock Setting - Only show if supported */}
          {isWakeLockSupported() && (
            <div className="border-t border-gray-200 dark:border-gray-600 pt-6 transition-colors duration-300">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Prayer Experience</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {keepScreenAwake ? (
                      <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <MonitorOff className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    )}
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-200">Keep Screen Awake</span>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        Prevents screen from dimming while prayer modal is open
                      </p>
                    </div>
                  </div>
                  
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={keepScreenAwake}
                      onChange={(e) => handleKeepScreenAwakeToggle(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Management - Only show for premium users */}
          {isPremiumUser && (
            <div className="border-t border-gray-200 dark:border-gray-600 pt-6 transition-colors duration-300">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Subscription</h3>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-purple-900 dark:text-purple-100">Premium Subscription Active</p>
                    <p className="text-sm text-purple-800 dark:text-purple-200 mt-1">
                      You have full access to all premium features.
                    </p>

                    {!showCancelConfirmation ? (
                      <button
                        onClick={() => setShowCancelConfirmation(true)}
                        className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Cancel Subscription
                      </button>
                    ) : (
                      <div className="mt-3 space-y-2">
                        <p className="text-sm font-medium text-red-900 dark:text-red-100">
                          Are you sure you want to cancel? You'll lose access at the end of your billing period.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelSubscription}
                            disabled={isCancelling}
                            className="bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
                          </button>
                          <button
                            onClick={() => setShowCancelConfirmation(false)}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Keep Subscription
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trial Status - Only show for trial users */}
          {isTrialActive && !isPremiumUser && (
            <div className="border-t border-gray-200 dark:border-gray-600 pt-6 transition-colors duration-300">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Trial Status</h3>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900 dark:text-blue-100">Free Trial Active</p>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                      {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                      {trialEndDate && ` • Ends ${new Date(trialEndDate).toLocaleDateString()}`}
                    </p>
                    {onUpgradeClick && (
                      <button
                        onClick={onUpgradeClick}
                        className="mt-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105"
                      >
                        Upgrade to Premium
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Theme Settings */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6 transition-colors duration-300">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Appearance</h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                {theme === 'light' ? (
                  <Sun className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mt-0.5" />
                ) : theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-blue-400 dark:text-blue-300 mt-0.5" />
                ) : (
                  <Laptop className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5" />
                )}
                <div className="flex-1">
                  <span className="font-medium text-gray-700 dark:text-gray-200">Theme</span>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 mb-3">
                    Choose your preferred color scheme
                  </p>
                  
                  <div className="space-y-2">
                    {([
                      { mode: 'light' as ThemeMode, icon: Sun, label: 'Light', description: 'Always use light theme' },
                      { mode: 'dark' as ThemeMode, icon: Moon, label: 'Dark', description: 'Always use dark theme' },
                      { mode: 'system' as ThemeMode, icon: Laptop, label: 'System', description: 'Follow your device setting' }
                    ]).map(({ mode, icon: Icon, label, description }) => (
                      <button
                        key={mode}
                        onClick={() => setTheme(mode)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          theme === mode
                            ? 'bg-indigo-50 dark:bg-indigo-900 border-2 border-indigo-200 dark:border-indigo-600'
                            : 'bg-gray-50 dark:bg-gray-700 border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${
                          theme === mode 
                            ? 'text-indigo-600 dark:text-indigo-400' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`} />
                        <div>
                          <div className={`font-medium ${
                            theme === mode 
                              ? 'text-indigo-900 dark:text-indigo-100' 
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {label}
                          </div>
                          <div className={`text-sm ${
                            theme === mode 
                              ? 'text-indigo-700 dark:text-indigo-300' 
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {description}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Developer/Debug Section */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6 transition-colors duration-300">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Developer Tools</h3>

            <div className="space-y-3">
              {/* Reset All Data Button */}
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-900 dark:text-red-100 mb-1">Reset All Data</p>
                    <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                      This will clear all local storage and reset the app to its initial state. All progress will be lost.
                    </p>

                    {!showResetConfirmation ? (
                      <button
                        onClick={() => setShowResetConfirmation(true)}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Reset App Data
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-red-900 dark:text-red-100">
                          Are you sure? This cannot be undone.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleResetAllData}
                            className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Yes, Reset Everything
                          </button>
                          <button
                            onClick={() => setShowResetConfirmation(false)}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};